import { CallAPI } from "../../apiManager/ApiCall";
import {
  addPinURL,
  addSlideURL,
  addSubTitleURL,
  addTitleURL,
  editPinURL,
  getSlideByIdURL,
  getTitleURL,
  getLibraryURL,
  libraryReferenceURL,
  libraryVisibilityURL,
  swapTitleURL,
  editTitleImageCallURL,
  updatePresentationPublishURL,
  updateTitleLockURL,
  updateTitleHiddenURL,
} from "../../apiManager/APIConfig";
import { setTitle } from "../../redux/slices/titleSlice";
import { store } from "../../redux/store";

/* Read the admin flag from the auth slice. Used as the default for
   `includeHidden` so any admin-initiated fetch (initial load, post-add
   refresh, post-edit refresh, etc.) automatically asks the server for
   hidden rows too. The backend still gates the param to admins only,
   so non-admins flipping this client-side helper would not bypass the
   filter. */
const currentUserIsAdmin = () => {
  try {
    const role = store.getState()?.auth?.role;
    return String(role || "").toLowerCase() === "admin";
  } catch {
    return false;
  }
};

/*
  `includeHidden` (admin-only opt-in): when true, the request asks the
  server to also return rows with `is_hidden = true`. The server only
  honours the flag for admin viewers — every other caller (including
  the mobile app) sees hidden rows stripped, regardless of the param.

  Default behaviour: if the caller does not pass an explicit boolean we
  auto-include hidden rows for admin users. This way add / edit /
  delete helpers below that re-fetch with `GetTitlesCall(lang, type)`
  keep showing hidden titles after the action — without each call site
  having to plumb the role through. Pass an explicit `true` / `false`
  to force a specific behaviour (e.g. the public-browse modal passes
  `false` to never include hidden rows even for an admin viewer).
*/
export const GetTitlesCall = async (lang, type, includeHidden, extraQuery = null) => {
  try {
    const effectiveInclude =
      typeof includeHidden === "boolean"
        ? includeHidden
        : currentUserIsAdmin();
    const params = new URLSearchParams({ lang, type });
    if (effectiveInclude) params.set("include_hidden", "true");
    if (extraQuery && typeof extraQuery === "object") {
      Object.entries(extraQuery).forEach(([key, value]) => {
        if (value != null && String(value).length) {
          params.set(key, String(value));
        }
      });
    }
    const response = await CallAPI({
      url: `${getTitleURL}?${params.toString()}`,
      method: "GET",
    });
    const list = Array.isArray(response) ? response : response?.data;
    if (Array.isArray(list)) {
      store.dispatch(setTitle({ type, list }));
      return list;
    }
    if (!response?.status) {
      const fallback = Array.isArray(response) ? response : [];
      if (fallback.length) {
        store.dispatch(setTitle({ type, list: fallback }));
      }
      return response;
    }
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Fetch titles without updating Redux (e.g. for browse-public modal). */
export const GetTitlesCallNoStore = async (lang, type, includeHidden = false) => {
  try {
    const params = new URLSearchParams({ lang, type });
    if (includeHidden) params.set("include_hidden", "true");
    const response = await CallAPI({
      url: `${getTitleURL}?${params.toString()}`,
      method: "GET",
    });
    const list = Array.isArray(response) ? response : response?.data;
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
};

export const AddTitlesCall = async (body) => {
  try {
    const response = await CallAPI({ url: addTitleURL, method: "POST", body });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddSlideCall = async (body) => {
  try {
    const response = await CallAPI({ url: addSlideURL, method: "POST", body });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddSubTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: addSubTitleURL,
      method: "POST",
      body,
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const EditSubTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addSubTitleURL}`,
      method: "PUT",
      body,
    });
    await GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const EditTitleImageCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${editTitleImageCallURL}`,
      method: "PUT",
      body,
    });
    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const EditTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addTitleURL}`,
      method: "PUT",
      body,
    });
    await GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const DeleteTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addTitleURL}/${body?.id}`,
      method: "DELETE",
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const DeleteSubTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${addSubTitleURL}/${body?.id}`,
      method: "DELETE",
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const GetSlideByIdCall = async (body) => {
  try {
    const includePrivate = body?.includePrivatePins ? "&includePrivatePins=true" : "";
    const ts = body?.ts ? `&_ts=${encodeURIComponent(body.ts)}` : "";
    const response = await CallAPI({
      url: `${getSlideByIdURL}?id=${body?.id}&lang=${body?.lang}${includePrivate}${ts}`,
      method: "GET",
    });

    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddPinCall = async (body) => {
  try {
    const response = await CallAPI({ url: addPinURL, method: "POST", body });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const EditPinCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${editPinURL}`,
      method: "PUT",
      body,
    });

    GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
export const DeletePinCall = async (id) => {
  try {
    const response = await CallAPI({
      url: `${editPinURL}/${id}`,
      method: "DELETE",
    });

    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const SwapTitlesCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${swapTitleURL}`,
      method: "PUT",
      body,
    });
    await GetTitlesCall(body?.lang, body?.type);
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

// Update publish / unpublish state for a presentation title.
// body: { title_id, is_published }
export const UpdatePresentationPublishCall = async (body) => {
  try {
    console.log("[WebPresentations] publish PUT", {
      title_id: body?.title_id,
      is_published: body?.is_published,
      url: updatePresentationPublishURL,
    });
    const response = await CallAPI({
      url: updatePresentationPublishURL,
      method: "PUT",
      body,
    });
    console.log("[WebPresentations] publish response", response);
    return response;
  } catch (error) {
    console.error("[WebPresentations] publish error", error);
    return { status: 400 };
  }
};

/*
  Admin-only: lock / unlock a catalogue slide (his / emb / anatomy / 3d models).
  body: { title_id, locked, lang, type }
   - title_id, locked → sent to the backend
   - lang, type       → used to refresh the local Redux list afterwards
                        so the badge updates without a full page reload
*/
export const UpdateTitleLockCall = async (body) => {
  try {
    const response = await CallAPI({
      url: updateTitleLockURL,
      method: "PUT",
      body: { title_id: body?.title_id, locked: !!body?.locked },
    });
    if (body?.lang && body?.type) {
      // The lock toggle is admin-only, so fetch the admin view (which
      // also includes hidden rows) — that way the local list stays in
      // sync with what the admin can see / manage.
      await GetTitlesCall(body.lang, body.type, true);
    }
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/*
  Admin-only: hide / unhide a title or subtitle from the catalogue
  listing (his / emb / anatomy / 3d models). Hidden rows are excluded
  from non-admin listing endpoints so they disappear on web (for
  non-admins) and mobile.

  body: { title_id, is_hidden, lang, type }
   - title_id, is_hidden → sent to the backend
   - lang, type          → used to refresh the local Redux list afterwards
                           so the row visually updates without a reload
*/
export const UpdateTitleHiddenCall = async (body) => {
  try {
    const response = await CallAPI({
      url: updateTitleHiddenURL,
      method: "PUT",
      body: { title_id: body?.title_id, is_hidden: !!body?.is_hidden },
    });
    if (body?.lang && body?.type) {
      // Hide toggle is admin-only — refresh with `include_hidden=true`
      // so the just-hidden row stays visible in the admin UI (with its
      // "Hidden" badge) and the admin can flip it back if needed.
      await GetTitlesCall(body.lang, body.type, true);
    }
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

// My Library: private content + referenced public titles
export const GetLibraryCall = async (lang = "en") => {
  try {
    const response = await CallAPI({
      url: `${getLibraryURL}?lang=${lang}`,
      method: "GET",
    });
    const hasPayload =
      response?.status === 200 ||
      Array.isArray(response?.private) ||
      Array.isArray(response?.referenced);
    if (hasPayload) {
      return { private: response.private || [], referenced: response.referenced || [] };
    }
    return { private: [], referenced: [] };
  } catch (error) {
    return { private: [], referenced: [] };
  }
};

// Library visibility per category (e.g. his / emb / three_d_model).
// Returns a map like: { his: 'none' | 'all' | 'private', emb: '...', three_d_model: '...' }
export const GetLibraryVisibilityCall = async () => {
  try {
    const response = await CallAPI({
      url: libraryVisibilityURL,
      method: "GET",
    });
    // Expect either { status: 200, data: { ... } } or a plain object.
    if (response?.status && response?.data) return response.data;
    if (response && !response?.status) return response;
    return {};
  } catch (error) {
    return {};
  }
};

// Update visibility for a single category.
// body: { category: 'his' | 'emb' | 'three_d_model', visibility: 'none' | 'all' | 'private' }
export const UpdateLibraryVisibilityCall = async (body) => {
  try {
    const response = await CallAPI({
      url: libraryVisibilityURL,
      method: "PUT",
      body,
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const AddLibraryReferenceCall = async (title_id, slide_id = null, target_subtitle_id = null, target_title_id = null, lang = null) => {
  try {
    const body = { title_id };
    if (slide_id) body.slide_id = slide_id;
    if (target_subtitle_id) body.target_subtitle_id = target_subtitle_id;
    if (target_title_id) body.target_title_id = target_title_id;
    if (lang) body.lang = lang;
    const response = await CallAPI({
      url: libraryReferenceURL,
      method: "POST",
      body,
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

export const RemoveLibraryReferenceCall = async (title_id, slide_id = null, target_subtitle_id = null) => {
  try {
    let url = `${libraryReferenceURL}/${title_id}`;
    if (slide_id && target_subtitle_id) {
      url += `?slide_id=${encodeURIComponent(slide_id)}&target_subtitle_id=${encodeURIComponent(target_subtitle_id)}`;
    }
    const response = await CallAPI({
      url,
      method: "DELETE",
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};

/** Per-user order of linked public slides under a private subtitle (does not change public catalog order). */
export const ReorderLibraryReferencesCall = async (target_subtitle_id, ordered_slide_ids) => {
  try {
    const response = await CallAPI({
      url: `${libraryReferenceURL}/order`,
      method: "PUT",
      body: { target_subtitle_id, ordered_slide_ids },
    });
    return response;
  } catch (error) {
    return { status: 400 };
  }
};
